import ApiService from '../UMUtils/apis';
import { ROLES } from '../../Utils/constants';
import { Role } from '../../Utils/types';
import { transformAdminRole } from '../../Utils/utils';

/** Form data for saving entities - includes entity fields plus form-specific fields */
type EntityFormData = Record<string, unknown>;

export class UserManagementService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  public get client() {
    return this.apiService.client;
  }

  public async getMetadata(entityType: string) {
    const repo = this.apiService.getRepository(entityType);
    if (!repo) {
        throw new Error(`Repository for entity type ${entityType} not found`);
    }
    const { status, data } = await repo.getMetadata();
    return { status, data };
  }

  public async fetchEntities(
    entityType: string,
    {
      search = null,
      filters = null,
      ordering = null,
      page = 0,
      rowsPerPage = undefined,
      entityId = undefined,
    }: {
      search?: string | null;
      filters?: string[] | null;
      ordering?: string | null;
      page?: number;
      rowsPerPage?: number;
      entityId?: string;
    } = {}
  ) {
    const repo = this.apiService.getRepository(entityType);
    if (!repo) {
      throw new Error(`API for entity type ${entityType} not found`);
    }

    if (entityId) {
      return await repo.get({ entityId });
    }

    return await repo.get({
      search: search || undefined,
      filters: filters || undefined,
      ordering: ordering || undefined,
      page: (page && page > 0) ? page.toString() : undefined,
      rowsPerPage: rowsPerPage?.toString(),
    });
  }

  public async saveEntity(
    entityType: string,
    itemDetails: EntityFormData,
    isCreate: boolean
  ) {
    const adminRole = itemDetails[ROLES.ELEOS_ADMINPORTAL_ADMIN] as boolean | undefined;
    const { [ROLES.ELEOS_ADMINPORTAL_ADMIN]: _, ...formData } = itemDetails;

    const transformedRoles = transformAdminRole(formData.roles as Role[] | undefined, adminRole);
    if (transformedRoles) {
      formData.roles = transformedRoles;
    }

    const repo = this.apiService.getRepository(entityType);
    if (!repo) {
      throw new Error(`API for entity type ${entityType} not found`);
    }

    if (isCreate) {
      return await repo.create(formData);
    } else {
      return await repo.update(itemDetails.id as string, formData);
    }
  }

  public async deleteEntity(entityType: string, entityId: string) {
    const repo = this.apiService.getRepository(entityType);
    if (!repo) {
      throw new Error(`API for entity type ${entityType} not found`);
    }

    return await repo.delete(entityId);
  }

  public async reactivateProvider(entityId: string) {
    return await this.apiService.providers.setAccountStatus(entityId, 'reactivate', {});
  }
}
